import { PortableText } from "@portabletext/react";

import type { ArtistType } from "@sorcery/cms";
import { ExternalLink, H2, H3, InternalLink, P } from "@sorcery/ui";
import { wrapTextNodes } from "@sorcery/utils/client/sanity";

/**
 * This component uses Portable Text to render artist bio content.
 *
 * You can learn more about Portable Text on:
 * https://www.sanity.io/docs/block-content
 * https://github.com/portabletext/react-portabletext
 * https://portabletext.org/
 *
 */
export const ArtistBioContent = ({
  content,
}: {
  content: ArtistType["bio"];
}) => {
  return (
    <PortableText
      value={content}
      components={{
        // For all other nodes - this ensures all text nodes have a persistent parent node
        // to handle the google translate chrome extension bug
        // https://github.com/facebook/react/issues/11538
        // https://martijnhols.nl/gists/everything-about-google-translate-crashing-react
        block: {
          /* title */
          h3: ({ children }) => (
            //  lol i know...
            <H2 className="mt-12 text-big-text-color">{wrapTextNodes(children)}</H2>
          ),
          /* subtitle */
          h4: ({ children }) => (
            //  lol i know...
            <H3 className="mt-6 text-big-text-color">{wrapTextNodes(children)}</H3>
          ),
          /* regular text */
          normal: ({ children }) => {
            return <P>{wrapTextNodes(children)}</P>;
          },
        },
        list: {
          bullet: ({ children }) => (
            <ul className="mb-4 ml-4 list-outside list-disc space-y-[0.5]">{children}</ul>
          ),
          number: ({ children }) => (
            <ol className="mb-4 ml-4 list-outside list-decimal space-y-[0.5]">{children}</ol>
          ),
        },
        listItem: ({ children }) => <li>{wrapTextNodes(children)}</li>,
        // Custom markup on external and internal cms links
        marks: {
          link: ({ value, children }) => {
            const { blank, href } = value;
            // default historical links that didnt have this field to open new tabs
            return (
              <ExternalLink
                href={href as string}
                color="primary"
                target={blank || typeof blank === "undefined" ? "_blank" : undefined}
                rel="noopener noreferrer"
              >
                {wrapTextNodes(children)}
              </ExternalLink>
            );
          },
          post: ({ value, children }) => {
            const { slug } = value;
            const href = `/news/${slug}`;
            return (
              <InternalLink href={href} target="_blank" color="primary">
                {children}
              </InternalLink>
            );
          },
          artist: ({ value, children }) => {
            const { slug } = value;
            const href = `/artists/${slug}`;
            return (
              <InternalLink href={href} target="_blank" color="primary">
                {children}
              </InternalLink>
            );
          },
        },
      }}
    />
  );
};
